/**
 * Domain utility functions for normalizing and processing domain names.
 */

/**
 * Strips the "www." subdomain from a domain while preserving all other subdomains.
 *
 * This function treats "www.example.com" as equivalent to "example.com" since they
 * typically serve the same content, while preserving legitimate subdomains like
 * "blog.example.com" or "app.example.com".
 *
 * @param domain - The domain to process (may be a full URL or just the domain)
 * @returns The domain with "www." stripped if present, otherwise unchanged
 *
 * @example
 * stripWwwFromDomain("www.example.com") // returns "example.com"
 * stripWwwFromDomain("https://www.example.com/path") // returns "example.com/path" (caller should normalize first)
 * stripWwwFromDomain("blog.example.com") // returns "blog.example.com" (preserved)
 * stripWwwFromDomain("www.blog.example.com") // returns "blog.example.com" (only leading www stripped)
 * stripWwwFromDomain("example.com") // returns "example.com" (no change)
 */
export function stripWwwFromDomain(domain: string): string {
	if (!domain?.trim()) {
		return domain;
	}

	// Remove leading "www." (case-insensitive) while preserving the rest
	// Only strip if www is at the very start of the domain portion
	return domain.replace(/^www\./i, "");
}

/**
 * Strips the "www." subdomain from a URL while preserving the rest of the URL structure.
 * Works with both full URLs and bare domains.
 *
 * @param url - The URL or domain to process
 * @returns The URL with "www." stripped from the domain portion
 *
 * @example
 * stripWwwFromUrl("https://www.example.com/path") // returns "https://example.com/path"
 * stripWwwFromUrl("http://www.example.com:8080/path") // returns "http://example.com:8080/path"
 * stripWwwFromUrl("www.example.com") // returns "example.com"
 * stripWwwFromUrl("https://blog.example.com") // returns "https://blog.example.com" (preserved)
 */
export function stripWwwFromUrl(url: string): string {
	if (!url?.trim()) {
		return url;
	}

	// Match www. after protocol (if present) or at the start
	// Handles: https://www., http://www., //www., and bare www.
	return url.replace(/^((?:https?:)?\/\/)?www\./i, "$1");
}

/**
 * Reduce anything domain-shaped (bare domain, full URL, domain with a path or
 * trailing slash) to a bare lowercase hostname with "www." stripped.
 *
 * Returns `null` — never an empty string — when there is no usable domain.
 * PERSON and CATEGORY properties have `domain === null`, and an empty string
 * here is actively dangerous: `anyUrl.includes("")` is `true` in JS, so an
 * unguarded substring check would tag every citation as brand-owned. Callers
 * must branch on `null` rather than feed a falsy domain into a comparison.
 *
 * @example
 * normalizeDomainOrNull("https://www.example.com/path") // "example.com"
 * normalizeDomainOrNull("blog.example.com")             // "blog.example.com"
 * normalizeDomainOrNull(null)                           // null
 * normalizeDomainOrNull("")                             // null
 */
export function normalizeDomainOrNull(
	raw: string | null | undefined,
): string | null {
	if (!raw?.trim()) {
		return null;
	}

	const trimmed = raw.trim();
	let hostname: string;
	try {
		const withProtocol = trimmed.includes("://")
			? trimmed
			: `https://${trimmed}`;
		hostname = new URL(withProtocol).hostname;
	} catch {
		// Not URL-parseable — fall back to lopping off any path/query by hand.
		hostname = trimmed.split("/")[0]!.split("?")[0]!;
	}

	const normalized = stripWwwFromDomain(hostname.toLowerCase());
	return normalized.trim() ? normalized : null;
}

/**
 * True when `url`'s hostname is `domain` or a subdomain of it.
 *
 * Replaces the `url.includes(domain)` substring checks that used to decide
 * `isOurDomain`. Those had two failure modes this function closes: an absent
 * domain matched everything (see {@link normalizeDomainOrNull}), and a present one
 * matched any URL merely *containing* the domain as a substring — so
 * "acme.com" hit `notacme.com` and `techcrunch.com/2024/acme.com-review`.
 *
 * @example
 * isUrlOnDomain("https://blog.acme.com/x", "acme.com")  // true
 * isUrlOnDomain("https://notacme.com", "acme.com")      // false
 * isUrlOnDomain("https://acme.com", null)               // false
 */
export function isUrlOnDomain(
	url: string | null | undefined,
	domain: string | null | undefined,
): boolean {
	const target = normalizeDomainOrNull(domain);
	if (!target) {
		return false;
	}

	const host = normalizeDomainOrNull(url);
	if (!host) {
		return false;
	}

	return host === target || host.endsWith(`.${target}`);
}

/** The subset of a Property needed to derive the domain its brand logic keys off. */
export interface PropertyFocalDomainSource {
	domain?: string | null;
	seedUrl?: string | null;
}

/**
 * The single domain that brand-ownership logic should key off for a property.
 *
 * BRAND subjects carry `domain`. PRODUCT subjects deliberately do NOT — a
 * product is a page or section of a parent brand's site, so it stores a
 * `seedUrl` (e.g. `https://acme.com/products/energy-drink`) instead. Every
 * pipeline site that did `property.domain ?? ""` therefore attributed exactly
 * zero own-domain citations to products (and, before {@link isUrlOnDomain}
 * landed, attributed *all* of them — `anyUrl.includes("")` is `true`).
 *
 * The seedUrl's host is the right fallback: the product's owned media really
 * does live on the parent domain, so `acme.com/blog/...` is owned media for the
 * energy-drink product exactly as it is for the Acme brand.
 *
 * Returns a **normalized** bare hostname — lowercase, `www.` stripped, no
 * scheme/path — i.e. the same shape {@link normalizeDomainOrNull} produces, so
 * the result can be fed straight into {@link isUrlOnDomain}, `normalizeDomain`
 * comparisons, or a prompt template without further massaging. Returns `null`,
 * never `""`, when the property has no usable domain at all (PERSON, CATEGORY,
 * or a PRODUCT with an unparseable seedUrl) — callers must branch on `null`
 * rather than substring-match a falsy domain.
 *
 * @example
 * propertyFocalDomain({ domain: "acme.com" })                                    // "acme.com"
 * propertyFocalDomain({ domain: "", seedUrl: "https://acme.com/p/x" })           // "acme.com"
 * propertyFocalDomain({ domain: null, seedUrl: "https://www.acme.com/p/x?a=1" }) // "acme.com"
 * propertyFocalDomain({ domain: null, seedUrl: "not a url" })                    // null
 * propertyFocalDomain({ domain: null, seedUrl: null })                           // null
 */
export function propertyFocalDomain(
	property: PropertyFocalDomainSource | null | undefined,
): string | null {
	const fromDomain = normalizeDomainOrNull(property?.domain);
	if (fromDomain) {
		return fromDomain;
	}

	const seedUrl = property?.seedUrl?.trim();
	if (!seedUrl) {
		return null;
	}

	// Strict parse: seedUrl is validated as an absolute http(s) URL on the way in
	// (see `urlLooksValid`), so anything `new URL` rejects is corrupt data, not a
	// bare domain we should guess at.
	try {
		return normalizeDomainOrNull(new URL(seedUrl).hostname);
	} catch {
		return null;
	}
}

/**
 * Should a persisted `DiscoveryCitation.isOurDomain` flag be believed?
 *
 * Only when the property actually HAS a domain. A PERSON or CATEGORY subject
 * owns no website, so it can own no citation — and reports generated before the
 * empty-domain match was fixed stamped `isOurDomain: true` on EVERY citation of
 * such a property (`anyUrl.includes("")` is `true`). Reading the flag unguarded
 * reports 100% owned media for those subjects.
 *
 * Every reader of `isOurDomain` should go through this rather than testing the
 * boolean directly, so an unrepaired row can't resurrect the bug.
 */
export function isOwnDomainCitation(
	isOurDomain: boolean | null | undefined,
	brandDomain: string | null | undefined,
): boolean {
	return Boolean(isOurDomain) && normalizeDomainOrNull(brandDomain) !== null;
}

/**
 * Build a synthetic, domain-free identifier for a PERSON "peer" competitor.
 *
 * The `Competitor.url` column is NOT NULL and unique per property, but peers
 * are individuals with no website. Share-of-voice matching keys off name +
 * aliases (never the url), so we store a sentinel derived from the peer's name.
 *
 * The `peer:` scheme guarantees the value can never be mistaken for a real
 * domain: `parse-domain` rejects it, and `normalizeDomainOrNull("peer:jane-doe")`
 * yields a value that never matches any citation host — so a peer can't
 * accidentally be counted as owning a citation.
 *
 * Stable for a given name so the `@@unique([url, propertyId])` constraint
 * dedupes re-adds of the same peer.
 *
 * @example
 * buildPeerUrl("Rand Fishkin")   // "peer:rand-fishkin"
 * buildPeerUrl("  A.  Chen! ")   // "peer:a-chen"
 */
export function buildPeerUrl(name: string): string {
	const slug = (name ?? "")
		.trim()
		.toLowerCase()
		.normalize("NFKD")
		.replace(/\p{M}/gu, "") // strip diacritics (combining marks after NFKD)
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
	return `peer:${slug || "unknown"}`;
}

/** True when a competitor url is a synthetic domain-free peer identifier. */
export function isPeerUrl(url: string | null | undefined): boolean {
	return typeof url === "string" && url.startsWith("peer:");
}
